import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { FaRegCopy, FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { changeApiKey } from 'app/entities/user-profile/change-api.reducer';
import { getSession } from 'app/shared/reducers/authentication';
import { Link } from 'react-router-dom';
import { colors, formatVND } from 'app/shared/theme/colors';
import axios from 'axios';
import { useTranslation } from 'app/shared/i18n';

export const UserProfile = () => {
  const { t } = useTranslation();
  const account = useAppSelector(state => state.authentication.account);
  const [showPw, setShowPw] = useState(false);
  const [allRanks, setAllRanks] = useState([]);
  const [totalDeposit, setTotalDeposit] = useState<number | null>(null);
  const [retentionCurrent, setRetentionCurrent] = useState<number | null>(null);
  const [retentionMax, setRetentionMax] = useState<number>(30);
  const dispatch = useAppDispatch();

  useEffect(() => {
    axios
      .get('/api/ranks')
      .then(res => setAllRanks(res.data))
      .catch(() => {
        /* ignore */
      });
    // Fetch total deposit in last 7 days (or period of current rank)
    axios
      .get('/api/deposit/sum-period')
      .then(res => setTotalDeposit(res.data?.data ?? 0))
      .catch(() => setTotalDeposit(0));
    // Fetch order retention config
    axios
      .get('/api/account/order-retention-config')
      .then(res => {
        setRetentionCurrent(res.data?.current ?? 7);
        setRetentionMax(res.data?.max ?? 30);
      })
      .catch(() => {
        /* ignore */
      });
  }, []);

  const onCopy = () => {
    navigator.clipboard.writeText(account.apiKey);
    toast.success(t('userProfile.copiedApiKey'));
  };
  const onChangeApiKey = async () => {
    await dispatch(changeApiKey());
    dispatch(getSession());
    toast.success(t('userProfile.changedApiKey'));
  };

  const sortedRanks = useMemo(() => [...allRanks].sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)), [allRanks]);
  const currentRank = account.rank;
  const nextRank = useMemo(() => {
    if (!currentRank || sortedRanks.length === 0) return sortedRanks[0] || null;
    const idx = sortedRanks.findIndex(r => r.id === currentRank.id);
    if (idx < 0 || idx >= sortedRanks.length - 1) return null;
    return sortedRanks[idx + 1];
  }, [currentRank, sortedRanks]);

  // Use totalDeposit (actual deposit in period) instead of wallet balance
  const depositInPeriod = totalDeposit ?? 0;

  const rankProgress = useMemo(() => {
    if (!nextRank) return 100;
    const nextMin = nextRank.minAmount || 0;
    if (nextMin <= 0) return 100;
    return Math.min(100, Math.max(0, (depositInPeriod / nextMin) * 100));
  }, [nextRank, depositInPeriod]);

  const amountNeeded = useMemo(() => {
    if (!nextRank) return 0;
    return Math.max(0, (nextRank.minAmount || 0) - depositInPeriod);
  }, [nextRank, depositInPeriod]);

  const periodDays = nextRank?.periodDays || currentRank?.periodDays || 7;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '36px 24px' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        {t('userProfile.title')}
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: sortedRanks.length > 0 ? '1fr 1fr' : '1fr', gap: 20, alignItems: 'flex-start' }}>
        <div className="ms-card" style={{ overflow: 'hidden', borderRadius: 12 }}>
          <div style={{ padding: 20 }}>
            {[
              { l: t('userProfile.account'), v: account.login },
              { l: 'Email', v: account.email },
              {
                l: t('userProfile.memberRank'),
                v: (
                  <span
                    className="ms-badge"
                    style={{ color: colors.accent, background: colors.accentDim, borderColor: `${colors.accent}33` }}
                  >
                    👑 {currentRank?.name || 'Member'}
                  </span>
                ),
              },
              {
                l: t('userProfile.balance'),
                v: (
                  <span style={{ fontWeight: 700, color: colors.success, fontFamily: "'JetBrains Mono', monospace" }}>
                    {formatVND(account.wallet ?? 0)}
                  </span>
                ),
              },
            ].map((r, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: i < 3 ? `1px solid ${colors.border}` : 'none',
                }}
              >
                <span style={{ fontSize: 13, color: colors.muted }}>{r.l}</span>
                <span style={{ fontSize: 13 }}>{r.v}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: '14px 20px', background: colors.surface, borderTop: `1px solid ${colors.border}` }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.dim,
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '.06em',
              }}
            >
              API Key
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                readOnly
                type={showPw ? 'text' : 'password'}
                value={showPw ? account.apiKey : '\u2022'.repeat(24)}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: 8,
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  color: colors.muted,
                  outline: 'none',
                }}
              />
              <button className="ms-btn ms-btn--ghost" onClick={() => setShowPw(!showPw)} style={{ padding: '7px 10px', fontSize: 14 }}>
                {showPw ? <FaRegEye /> : <FaRegEyeSlash />}
              </button>
              <button className="ms-btn ms-btn--ghost" onClick={onCopy} style={{ padding: '7px 10px', fontSize: 14 }}>
                <FaRegCopy />
              </button>
            </div>
          </div>

          <div style={{ padding: '14px 20px', display: 'flex', gap: 8 }}>
            <Link
              to="/account/password"
              className="ms-btn ms-btn--secondary"
              style={{ flex: 1, fontSize: 13, textDecoration: 'none', textAlign: 'center' }}
            >
              {t('userProfile.changePassword')}
            </Link>
            <button className="ms-btn ms-btn--primary" onClick={onChangeApiKey} style={{ flex: 1, fontSize: 13 }}>
              {t('userProfile.changeApiKey')}
            </button>
          </div>

          {/* Order Retention */}
          {retentionCurrent !== null && (
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${colors.border}` }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.dim,
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                }}
              >
                {t('orderRetention.title')}
              </div>
              <p style={{ fontSize: 12, color: colors.muted, margin: '0 0 10px 0' }}>{t('orderRetention.description')}</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {[1, 3, 7, 14, 30]
                  .filter(d => d <= retentionMax)
                  .map(d => (
                    <button
                      key={d}
                      onClick={async () => {
                        try {
                          await axios.put('/api/account/order-retention', { days: d });
                          setRetentionCurrent(d);
                          toast.success(t('orderRetention.saveSuccess'));
                        } catch {
                          toast.error(t('common.error'));
                        }
                      }}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 999,
                        border: `1px solid ${retentionCurrent === d ? colors.accent : colors.border}`,
                        background: retentionCurrent === d ? colors.accentDim : 'transparent',
                        color: retentionCurrent === d ? colors.accent : colors.muted,
                        fontWeight: retentionCurrent === d ? 700 : 500,
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'all .2s',
                      }}
                    >
                      {d} {t('orderRetention.days')}
                    </button>
                  ))}
              </div>
              <div style={{ fontSize: 11, color: colors.dim, marginTop: 8 }}>
                {t('orderRetention.max')}: {retentionMax} {t('orderRetention.days')}
              </div>
            </div>
          )}
        </div>

        {/* Rank progress */}
        {sortedRanks.length > 0 ? (
          <div className="ms-card" style={{ overflow: 'hidden', borderRadius: 12 }}>
            <div
              style={{
                padding: '14px 20px',
                background: `linear-gradient(135deg, ${colors.surface}, ${colors.bgCard})`,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 700 }}>{t('userProfile.membershipTitle')}</div>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: colors.dim, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                    {t('userProfile.currentRank')}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.accent }}>{currentRank?.name || 'Member'}</div>
                </div>
                {currentRank?.discountPercent > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 11, color: colors.dim }}>{t('userProfile.benefit')}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: colors.success }}>-{currentRank.discountPercent}%</div>
                  </div>
                )}
              </div>

              {/* Deposit in period info */}
              <div
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  marginBottom: 16,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: 12, color: colors.muted }}>
                  {t('userProfile.totalDeposit')} {periodDays} {t('userProfile.daysAgo')}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: colors.text, fontFamily: "'JetBrains Mono', monospace" }}>
                  {formatVND(depositInPeriod)}
                </span>
              </div>

              {nextRank ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: colors.muted }}>
                      {t('userProfile.progressTo')} <strong style={{ color: colors.text }}>{nextRank.name}</strong>
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                      {rankProgress.toFixed(0)}%
                    </span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: colors.surface, overflow: 'hidden', marginBottom: 12 }}>
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${colors.accent}, #16a34a)`,
                        width: `${rankProgress}%`,
                        transition: 'width .5s ease',
                      }}
                    />
                  </div>

                  <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        fontSize: 11,
                        fontWeight: 600,
                        color: colors.dim,
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                      }}
                    >
                      <div style={{ padding: '8px 12px', background: colors.surface }} />
                      <div
                        style={{
                          padding: '8px 12px',
                          background: colors.surface,
                          textAlign: 'center',
                          borderLeft: `1px solid ${colors.border}`,
                        }}
                      >
                        {currentRank?.name || 'Member'}
                      </div>
                      <div
                        style={{
                          padding: '8px 12px',
                          background: colors.accentDim,
                          textAlign: 'center',
                          borderLeft: `1px solid ${colors.border}`,
                          color: colors.accent,
                        }}
                      >
                        {nextRank.name}
                      </div>
                    </div>
                    {[
                      { l: t('userProfile.discount'), cur: `${currentRank?.discountPercent || 0}%`, next: `${nextRank.discountPercent}%` },
                      {
                        l: t('userProfile.minDeposit'),
                        cur: formatVND(currentRank?.minAmount || 0),
                        next: formatVND(nextRank.minAmount || 0),
                      },
                      {
                        l: t('userProfile.within'),
                        cur: `${currentRank?.periodDays || 7} ${t('userProfile.days')}`,
                        next: `${nextRank.periodDays || 7} ${t('userProfile.days')}`,
                      },
                    ].map((row, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderTop: `1px solid ${colors.border}` }}>
                        <div style={{ padding: '10px 12px', fontSize: 12, color: colors.muted }}>{row.l}</div>
                        <div
                          style={{
                            padding: '10px 12px',
                            fontSize: 12,
                            color: colors.text,
                            textAlign: 'center',
                            borderLeft: `1px solid ${colors.border}`,
                            fontFamily: "'JetBrains Mono', monospace",
                          }}
                        >
                          {row.cur}
                        </div>
                        <div
                          style={{
                            padding: '10px 12px',
                            fontSize: 12,
                            color: colors.accent,
                            textAlign: 'center',
                            borderLeft: `1px solid ${colors.border}`,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontWeight: 600,
                          }}
                        >
                          {row.next}
                        </div>
                      </div>
                    ))}
                  </div>

                  {amountNeeded > 0 && (
                    <div
                      style={{
                        marginTop: 14,
                        padding: '12px 14px',
                        borderRadius: 10,
                        background: colors.accentDim,
                        border: `1px solid ${colors.accent}22`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span style={{ fontSize: 18 }}>💡</span>
                      <div style={{ fontSize: 13, color: colors.text }}>
                        {t('userProfile.depositMore')}{' '}
                        <strong style={{ color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                          {formatVND(amountNeeded)}
                        </strong>{' '}
                        {t('userProfile.inDays')} {nextRank.periodDays || 7} {t('userProfile.toUpgrade')}{' '}
                        <strong style={{ color: colors.accent }}>{nextRank.name}</strong> {t('userProfile.andGetDiscount')}{' '}
                        <strong style={{ color: colors.success }}>{nextRank.discountPercent}%</strong>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '16px 0', textAlign: 'center' }}>
                  <span style={{ fontSize: 24 }}>🏆</span>
                  <div style={{ fontSize: 14, fontWeight: 600, color: colors.success, marginTop: 6 }}>{t('userProfile.highestRank')}</div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {/* end grid */}
    </div>
  );
};
